import _ from 'lodash';
import { store } from 'src/stores/store';
import mixpanel, { Mixpanel } from 'mixpanel';
import { integrations } from 'src/config';
import { Mixpanel as MixpanelBrowser } from 'mixpanel-browser';
import mixpanelBrowser from 'src/integrations/mixpanel';
import { FilterSettingsType } from 'src/models/filter-settings';
import { SemanticSearch } from 'src/models/search';

class MixpanelV3Helper {
  mixpanelClient: Mixpanel;
  mixpanelBrowser: MixpanelBrowser;

  constructor() {
    this.mixpanelClient = mixpanel.init(integrations.mixpanelV3.projectKey, {
      debug: integrations.mixpanelV3.debug,
      $city: true,
      $region: true,
      mp_country_code: true,
      $geo_source: true
    });
    this.mixpanelBrowser = mixpanelBrowser.AISearchAndDiscovery;
  }

  getTenantApp() {
    return _.get(store.getState(), 'oauth.tenantApp', {});
  }

  getIsSimulator() {
    return _.get(store.getState(), 'oauth.isSimulator', false);
  }

  getBoostPlan(tenantApp) {
    return _.get(tenantApp, 'selectedPlan', '').replace('_discount', '') || 'Trial';
  }

  getSuperProperties(): Record<String, any> {
    const tenantApp = this.getTenantApp();
    return {
      distinct_id: tenantApp.tenantId,
      tenantId: tenantApp.tenantId,
      boostPlan: this.getBoostPlan(tenantApp),
      shopifyPlan: _.get(tenantApp, 'externalInfo.plan_display_name', ''),
      noProducts: tenantApp.appParams?.currentTotalProducts,
      name: tenantApp.externalInfo?.name,
      language: tenantApp.appLanguage,
      country: tenantApp.externalInfo?.country_name
    };
  }

  createTenantProfile(): void {
    const tenantApp = this.getTenantApp();
    if (_.isEmpty(tenantApp)) {
      return;
    }
    this.mixpanelClient.people.set(tenantApp.tenantId, {
      $name: tenantApp.externalInfo?.name,
      $country_code: tenantApp.externalInfo?.country_name,
      shopifyPlan: _.get(tenantApp, 'externalInfo.plan_display_name', ''),
      boostPlan: this.getBoostPlan(tenantApp)
    });
  }

  trackEventData(eventName: string, properties: Record<String, any> = {}): void {
    if (this.getIsSimulator()) {
      return;
    }
    this.createTenantProfile();
    this.mixpanelBrowser.track(eventName, { ...this.getSuperProperties(), ...properties });
  }

  trackCreateMerchandiseRule(payload: CreateMerchandiseRulePayload = {}) {
    const EVENT_NAME_MAPPING = {
      pin: 'Create a Pin Rule',
      boost: 'Create a Boost Rule',
      demote: 'Create a Demote Rule',
      hide: 'Create a Hide Rule',
      filter: 'Create a Filter Rule'
    };
    const strategyType = payload.strategies?.[0]?.type;

    // Strategy: pin
    if (strategyType === 'pin') {
      this.trackEventData(EVENT_NAME_MAPPING[strategyType], {
        feature: 'Merchandising V2',
        pageType: payload.ruleType,
        selectedStrategy: strategyType,
        selectedMatchPin: payload.strategies?.[0]?.pinMatchRequired,
        numberPinProducts: payload?.strategies?.[0]?.pinPositions?.filter(e => e.id)?.length
      });
      return;
    }

    // Other strategy
    const trackingAttributes: string[] = [];
    for (const strategy of payload.strategies) {
      (strategy.attributes || []).forEach(e => {
        trackingAttributes.push(e.attribute);
      });
    }
    this.trackEventData(EVENT_NAME_MAPPING[strategyType], {
      feature: 'Merchandising V2',
      pageType: payload.ruleType,
      selectedStrategy: strategyType,
      attributes: trackingAttributes,
      numberAttributes: trackingAttributes.length
    });
  }

  trackUpdateSearchSetting(currentSearchSetting: SearchSetting, newSearchSetting: SearchSetting) {
    // Search fields settings
    const currentSearchFields = currentSearchSetting.searchFields.sort((a, b) => a.name - b.name);
    const newSearchFields = newSearchSetting.searchFields.sort((a, b) => a.name - b.name);
    for (let i = 0; i < currentSearchFields.length; i++) {
      if (currentSearchFields[i].status !== newSearchFields[i].status) {
        const trackingSearchField = {};
        newSearchFields.forEach(e => {
          trackingSearchField[e.name] = e?.searchWeight || 'non-searchable field';
        });
        this.trackEventData('Edit Search fields settings', {
          feature: 'Search Settings',
          ...trackingSearchField
        });
        break;
      }
    }
  }

  trackInCollectionSetting(currentSearchSetting: SearchSetting, newSearchSetting: SearchSetting) {
    // In-collection search settings
    if (
      currentSearchSetting.inCollectionSearch?.collectionSearchLabel !==
        newSearchSetting.inCollectionSearch?.collectionSearchLabel ||
      currentSearchSetting.inCollectionSearch?.enableCollectionSearch !==
        newSearchSetting.inCollectionSearch?.enableCollectionSearch
    ) {
      this.trackEventData('Edit In-collection search settings', {
        feature: 'Search Settings',
        searchOnCollectionPage: newSearchSetting.inCollectionSearch?.enableCollectionSearch
      });
    }
  }

  trackUpdateFilterSettings(type: String, data: Record<String, any>): void {
    if (type === 'filterLayout') {
      this.trackEventData('Change Filter Layout', {
        feature: 'Filter Layout',
        verticalLayout: data?.general?.filterTreeVerticalStyle,
        horizontalLayout: data?.general?.filterTreeHorizontalStyle,
        mobileLayout: data?.general?.filterTreeMobileStyle,
        stickyOnMobile: data?.general?.stickyFilterOnMobile,
        stickyOnDesktop: data?.general?.stickyFilterOnDesktop
      });
    }
    if (type === 'filterSettings') {
      this.trackEventData('Change Filter Settings', {
        feature: 'Filter Settings'
      });
    }
    if (type === 'instantSearchDisplay') {
      const layoutSettingsMapping = {
        style1: 'One Column',
        style2: 'Default - Two Columns',
        style3: 'Overlay Fullwidth'
      };
      // Tracking when enable toggle instant search
      if (data.isEnableSuggestionTracking) {
        this.trackEventData('Enable Instant Search ', {
          feature: 'Instant Search Display',
          layoutSettings: layoutSettingsMapping[data?.suggestionStyle]
        });
        return;
      }
      // Tracking when edit instant search
      if (data?.search?.enableSuggestion) {
        const dataTracking = this.buildDataEventEditISW(data);
        this.trackEventData('Edit Instant Search Display', {
          feature: 'Instant Search Display',
          ...dataTracking
        });
      } else {
        // Tracking when disabled instant search
        this.trackEventData('Disable Instant Search ', {
          feature: 'Instant Search Display',
          layoutSettings: layoutSettingsMapping[data?.search?.suggestionStyle]
        });
      }
    }
    if (type === 'searchPageDisplay') {
      this.trackEventData('Edit Search Results Page', {
        feature: 'Search Page Display',
        disResultCollection: data?.search?.searchPanelBlocks?.collections?.active,
        disResultPages: data?.search?.searchPanelBlocks?.pages?.active,
        disSearchTips: data?.search?.searchPanelBlocks?.searchTips?.active,
        disSearchTermSuggest: data?.search?.searchPanelBlocks?.searchTermSuggestions?.active,
        disPopularProducts: data?.search?.searchPanelBlocks?.mostPopularProducts?.active
      });
    }
    if (type === 'translation') {
      this.trackEventData('Change Translation', {
        feature: 'Translation'
      });
    }
  }

  trackEditSuggestionDictionary(oldSuggestions: any[], newSuggestions: any[]): void {
    let totalChangedSuggestion = 0;
    for (let i = 0; i < newSuggestions.length; i++) {
      if (!_.isEqual(oldSuggestions[i], newSuggestions[i])) {
        totalChangedSuggestion += 1;
      }
    }
    this.trackEventData('Edit Suggestion Dictionary', {
      feature: 'Suggestion Dictionary',
      noSuggestion: totalChangedSuggestion
    });
  }

  trackUpdateStopWord(oldStopWords: string[], newStopWords: string[]): void {
    let totalAddedStopWords = 0;
    let totalDeletedStopWords = 0;
    newStopWords.forEach(e => {
      if (!oldStopWords.includes(e)) {
        totalAddedStopWords += 1;
      }
    });

    oldStopWords.forEach(e => {
      if (!newStopWords.includes(e)) {
        totalDeletedStopWords += 1;
      }
    });

    if (totalAddedStopWords) {
      this.trackEventData('Add Stop Words', {
        feature: 'Stop Words',
        noStopWords: totalAddedStopWords
      });
    }
    if (totalDeletedStopWords) {
      this.trackEventData('Delete Stop Words', {
        feature: 'Stop Words',
        noStopWords: totalDeletedStopWords
      });
    }
  }

  trackEditRedirectRules(oldRedirects: any[], newRedirects: any[]): void {
    let totalChangeRedirect = 0;
    for (let i = 0; i < newRedirects.length; i++) {
      if (!_.isEqual(oldRedirects[i], newRedirects[i])) {
        totalChangeRedirect += 1;
      }
    }
    this.trackEventData('Edit Redirect Rules', {
      feature: 'Redirect',
      noRedirect: totalChangeRedirect
    });
  }

  trackSelectAnalyticsFrame(): void {
    const path = window.location.pathname;
    const pathNames = path.substring(1).split('/');
    this.trackEventData('Select Analytics Frame', {
      feature: 'Analytics',
      analyticsType: pathNames?.[0],
      subType: pathNames?.[1] || 'overview'
    });
  }

  trackOpenSubscriptionReview(addOn: String, fromPage: string, buttonText: string): void {
    this.trackEventData('Unlock Add-on request', {
      addOn,
      page: fromPage,
      buttonText
    });
  }

  trackChangeThemeElement(
    oldSetting: Record<String, any>,
    newSetting: Record<String, any>,
    themeName: string,
    addedElements: Record<String, any>[],
    removedElements: Record<String, any>[]
  ): void {
    const flattenOldSetting = this.flattenObj(oldSetting);
    const flattenNewSetting = this.flattenObj(newSetting);
    const flattenResult = {};

    for (const key of Object.keys(flattenNewSetting)) {
      if (_.isNil(flattenOldSetting[key])) {
        flattenResult[key] = { type: 'new', value: flattenNewSetting[key] };
        continue;
      }

      if (flattenOldSetting[key] !== flattenNewSetting[key]) {
        flattenResult[key] = {
          type: 'change',
          from: flattenOldSetting[key],
          to: flattenNewSetting[key]
        };
      }
    }

    for (const key of Object.keys(flattenOldSetting)) {
      if (_.isNil(flattenNewSetting[key])) {
        flattenResult[key] = { type: 'remove', value: flattenOldSetting[key] };
      }
    }
    const expandResult = this.expandObj(flattenResult, '.');
    for (const key of Object.keys(expandResult)) {
      // check element is already created event
      let element = _.find(addedElements, item => item.elementName === key) || {};
      if (_.isEmpty(element))
        element = _.find(removedElements, item => item.elementName === key) || {};

      if (_.isEmpty(element)) {
        this.trackEventData('Change element settings', {
          feature: 'Theme Settings',
          elementName: key,
          selectedTheme: themeName,
          dataChange: expandResult[key]
        });
      }
    }
  }

  trackSemanticSearch = (oldSettings: SemanticSearch, settings: SemanticSearch) => {
    if (oldSettings?.enable === false && settings?.enable === true) {
      this.trackEventData('Enable Semantic Search', {
        feature: 'Standard Search'
      });
    }

    if (oldSettings?.enable === true && settings?.enable === false) {
      this.trackEventData('Disable Semantic Search', {
        feature: 'Standard Search'
      });
    }
  };

  flattenObj(ob) {
    let result = {};

    for (const i in ob) {
      if (typeof ob[i] === 'object' && !Array.isArray(ob[i])) {
        const temp = this.flattenObj(ob[i]);
        for (const j in temp) {
          result[i + '.' + j] = temp[j];
        }
      } else {
        result[i] = ob[i];
      }
    }
    return result;
  }

  expandObj(target, keySeparator) {
    var result = {};
    for (var key in target) {
      if (target.hasOwnProperty(key)) {
        var nestedKeys = key.split(keySeparator);
        // Get the last subKey
        var leaf = nestedKeys[nestedKeys.length - 1];
        // Get all subKeys except for the last
        var branch = nestedKeys.slice(0, nestedKeys.length - 1);

        var currentTarget = result;
        for (var i = 0; i < branch.length; i += 1) {
          var subKey = nestedKeys[i];
          // If this is the first time visiting this branch, we need to instantiate it
          if (currentTarget[subKey] === undefined) {
            currentTarget[subKey] = {};
          }
          // Visit the branch
          currentTarget = currentTarget[subKey];
        }
        currentTarget[leaf] = target[key];
      }
    }
    return result;
  }

  getMappingAddOnName(): Record<String, String> {
    return {
      aiPowerSearch: 'AI-powered-search',
      visualMerchandising: 'visual-merchandising',
      aiPowerRecommendation: 'AI-powered-recommendation',
      syncFrequency: 'sync-frequency',
      variantDisplay: 'variant-display',
      analytics: 'analytics'
    };
  }

  buildDataEventEditISW = (data: FilterSettingsType): Record<String, any> => {
    const suggestionBlocks = _.get(data, 'search.suggestionBlocks', []);
    const productsBlock = suggestionBlocks.find(
      suggestionBlock => suggestionBlock.type === 'products'
    );
    const collectionsBlock = suggestionBlocks.find(
      suggestionBlock => suggestionBlock.type === 'collections'
    );
    const suggestionsBlock = suggestionBlocks.find(
      suggestionBlock => suggestionBlock.type === 'suggestions'
    );
    const pagesBlock = suggestionBlocks.find(suggestionBlock => suggestionBlock.type === 'pages');

    return {
      disProduct: !!(_.get(productsBlock, 'status', 'active') === 'active'),
      disCollection: !!(_.get(collectionsBlock, 'status', 'active') === 'active'),
      disPopularSuggestions: !!(_.get(suggestionsBlock, 'status', 'active') === 'active'),
      disBlogAndPage: !!(_.get(pagesBlock, 'status', 'active') === 'active'),
      disSearchTermSuggest: _.get(data, 'search.suggestionNoResult.search_terms.status', false),
      disPopularProducts: _.get(data, 'search.suggestionNoResult.products.status', false),
      disRecentSearches: _.get(data, 'search.searchBoxOnclick.recentSearch.status', true),
      disSearchTermSuggestInSearchBox: _.get(
        data,
        'search.searchBoxOnclick.searchTermSuggestion.status',
        false
      ),
      disPopularProductsInSearchBox: _.get(
        data,
        'search.searchBoxOnclick.productSuggestion.status',
        false
      )
    };
  };
}
export const mixpanelV3Helper = new MixpanelV3Helper();

interface CreateMerchandiseRulePayload {
  name: String;
  queries: string[];
  collection: String;
  ruleType: String;
  status: String;
  strategies: [
    {
      type: string,
      attributes?: [
        {
          attribute: string,
          conductor: string,
          value: string
        }
      ],
      pinMatchRequired?: true,
      pinPositions: [
        {
          id?: number,
          product_id?: number,
          position: number
        }
      ]
    }
  ];
  effectFrom: number;
  effectTo: number;
}

interface SearchSetting {
  fuzzySearch: String;
  inCollectionSearch: {
    collectionSearchLabel: String,
    enableCollectionSearch: Boolean
  };
  minimumMustMatch: {
    threeWordsAndAbove: number,
    twoWords: number
  };
  searchFields: {
    name: String,
    status: Boolean,
    searchWeight: String
  }[];
}
